/**
 * DemandJourneyAnalytics - Sprint 10 Phase 2
 * Dashboard for demand lifecycle analytics and funnel visualization
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  BarChart3, PieChart, TrendingUp, TrendingDown, Clock, Users,
  ArrowRight, RefreshCw, CheckCircle, XCircle, AlertTriangle,
  Sparkles, ExternalLink, Filter, Calendar, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, LineChart, Line, Legend, Area, AreaChart
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_COLORS = {
  external_intake: '#06B6D4',
  intake: '#3B82F6',
  assessment: '#F59E0B',
  governance: '#8B5CF6',
  approved: '#10B981',
  rejected: '#EF4444'
};

const DemandJourneyAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [expandedSection, setExpandedSection] = useState('funnel');

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('simpliview_token');
    // TENANT ISOLATION: Include X-Tenant-ID header for sysadmin impersonation
    const impersonatedTenantId = localStorage.getItem('impersonated_tenant_id');
    const userTenantId = localStorage.getItem('simpliview_user_tenant_id');
    const activeTenantId = impersonatedTenantId || userTenantId;
    
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    
    if (activeTenantId) {
      headers['X-Tenant-ID'] = activeTenantId;
    }
    
    return headers;
  }, []);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/reporting/demands/journey?days=${period}`,
        { headers: getAuthHeaders() }
      );

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.data);
      } else {
        toast.error('Failed to load analytics');
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Prepare chart data
  const stageDistributionData = analytics?.stage_distribution?.counts
    ? Object.entries(analytics.stage_distribution.counts).map(([name, value]) => ({
        name: name.replace('_', ' ').charAt(0).toUpperCase() + name.replace('_', ' ').slice(1),
        value,
        fill: STAGE_COLORS[name] || '#6B7280'
      }))
    : [];

  const funnelData = analytics?.funnel_metrics
    ? [
        { name: 'Intake → Assessment', rate: analytics.funnel_metrics.intake_to_assessment?.rate || 0 },
        { name: 'Assessment → Governance', rate: analytics.funnel_metrics.assessment_to_governance?.rate || 0 },
        { name: 'Governance → Approved', rate: analytics.funnel_metrics.governance_to_approved?.rate || 0 }
      ]
    : [];

  const trendData = analytics?.trends?.daily || [];

  const timeInStageData = analytics?.time_in_stage
    ? Object.entries(analytics.time_in_stage).map(([stage, data]) => ({
        stage: stage.charAt(0).toUpperCase() + stage.slice(1),
        average: data.average_days,
        threshold: data.sla_threshold_days,
        compliant: data.sla_compliant
      }))
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="journey-analytics">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            Demand Journey Analytics
          </h1>
          <p className="text-gray-500 mt-1">
            Funnel metrics, stage performance, and lifecycle insights
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <Button variant="outline" onClick={fetchAnalytics}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">Total Demands</p>
                <p className="text-2xl font-bold">{analytics?.stage_distribution?.total || 0}</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 font-medium">Approved</p>
                <p className="text-2xl font-bold">{analytics?.stage_distribution?.counts?.approved || 0}</p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600 font-medium">Rejected</p>
                <p className="text-2xl font-bold">{analytics?.stage_distribution?.counts?.rejected || 0}</p>
              </div>
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 font-medium">Approval Rate</p>
                <p className="text-2xl font-bold">
                  {analytics?.funnel_metrics?.overall_approval_rate?.approval_rate || 0}%
                </p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-50 to-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-cyan-600 font-medium">External Queue</p>
                <p className="text-2xl font-bold">{analytics?.external_intake?.pending_review || 0}</p>
              </div>
              <div className="p-2 bg-cyan-100 rounded-lg">
                <ExternalLink className="h-5 w-5 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-50 to-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-indigo-600 font-medium">AI Adoption</p>
                <p className="text-2xl font-bold">{analytics?.ai_adoption?.adoption_rate || 0}%</p>
              </div>
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Sparkles className="h-5 w-5 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Stage Distribution Pie */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Stage Distribution
            </CardTitle>
            <CardDescription>Current demands by lifecycle stage</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={stageDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {stageDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-3 mt-4">
              {stageDistributionData.map((entry) => (
                <div key={entry.name} className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.fill }} />
                  <span>{entry.name}: {entry.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Funnel Conversion Rates */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Conversion Funnel
            </CardTitle>
            <CardDescription>Stage-to-stage conversion rates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnelData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} unit="%" />
                  <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => [`${value}%`, 'Conversion Rate']} />
                  <Bar dataKey="rate" fill="#6366F1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4 text-center">
              {funnelData.map((item, idx) => (
                <div key={idx} className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">{item.name}</p>
                  <p className={`text-lg font-bold ${item.rate >= 50 ? 'text-green-600' : item.rate >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {item.rate}%
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Time in Stage & Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Time in Stage */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Average Time in Stage
            </CardTitle>
            <CardDescription>Days spent per stage vs SLA threshold</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timeInStageData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="stage" />
                  <YAxis label={{ value: 'Days', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="average" name="Average Days" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="threshold" name="SLA Threshold" fill="#E5E7EB" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex gap-4 mt-4">
              {timeInStageData.map((item) => (
                <Badge
                  key={item.stage}
                  variant={item.compliant ? 'default' : 'destructive'}
                  className="flex items-center gap-1"
                >
                  {item.compliant ? <CheckCircle className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                  {item.stage}: {item.average}d / {item.threshold}d SLA
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Daily Trends */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Daily Activity Trends
            </CardTitle>
            <CardDescription>Demands created, transitions, and approvals</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="created" name="Created" stackId="1" stroke="#3B82F6" fill="#93C5FD" />
                  <Area type="monotone" dataKey="transitions" name="Transitions" stackId="2" stroke="#8B5CF6" fill="#C4B5FD" />
                  <Area type="monotone" dataKey="approvals" name="Approvals" stackId="3" stroke="#10B981" fill="#6EE7B7" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* External Intake & AI Adoption */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* External Intake Metrics */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ExternalLink className="h-5 w-5" />
              External Intake Performance
            </CardTitle>
            <CardDescription>Public submission funnel</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-cyan-50 rounded-lg">
                <span className="text-sm font-medium">Total Submissions</span>
                <span className="text-xl font-bold text-cyan-700">
                  {analytics?.external_intake?.total_submissions || 0}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-yellow-50 rounded-lg text-center">
                  <p className="text-xs text-yellow-600">Pending Review</p>
                  <p className="text-lg font-bold text-yellow-700">
                    {analytics?.external_intake?.pending_review || 0}
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-xs text-green-600">Converted</p>
                  <p className="text-lg font-bold text-green-700">
                    {analytics?.external_intake?.converted_to_internal || 0}
                  </p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg text-center">
                  <p className="text-xs text-red-600">Rejected</p>
                  <p className="text-lg font-bold text-red-700">
                    {analytics?.external_intake?.rejected || 0}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                <span className="text-sm font-medium">Conversion Rate</span>
                <span className="text-xl font-bold text-indigo-700">
                  {analytics?.external_intake?.conversion_rate || 0}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AI Feature Adoption */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              AI Feature Adoption
            </CardTitle>
            <CardDescription>AI-assisted demand shaping metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                <span className="text-sm font-medium">AI-Shaped Demands</span>
                <div className="text-right">
                  <span className="text-xl font-bold text-indigo-700">
                    {analytics?.ai_adoption?.ai_shaped_demands || 0}
                  </span>
                  <span className="text-sm text-indigo-500 ml-1">
                    / {analytics?.ai_adoption?.total_demands || 0}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <span className="text-sm font-medium">Adoption Rate</span>
                <span className="text-xl font-bold text-purple-700">
                  {analytics?.ai_adoption?.adoption_rate || 0}%
                </span>
              </div>
              
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-2">AI Actions This Period</p>
                <p className="text-2xl font-bold text-gray-700">
                  {analytics?.ai_adoption?.ai_actions_period || 0}
                </p>
              </div>
              
              {analytics?.ai_adoption?.action_breakdown && Object.keys(analytics.ai_adoption.action_breakdown).length > 0 && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">Action Breakdown</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(analytics.ai_adoption.action_breakdown).map(([action, count]) => (
                      <Badge key={action} variant="outline" className="text-xs">
                        {action.replace('ai_', '').replace('_', ' ')}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer Info */}
      <div className="mt-6 text-center text-sm text-gray-500">
        <p>
          Data generated at {analytics?.generated_at ? new Date(analytics.generated_at).toLocaleString() : '-'}
          {' · '}Analysis period: {period} days
        </p>
      </div>
    </div>
  );
};

export default DemandJourneyAnalytics;
